import type {
  MassiveStockResponse,
  MassiveOptionsResponse,
  StockBar,
  OptionSnapshot,
  HistoricalClose,
} from '../types.ts';

const MASSIVE_API_BASE_URL = 'https://api.massive.com';

export class MassiveApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'MassiveApiError';
  }
}

export async function massiveGet<T>(path: string): Promise<T> {
  const separator = path.includes('?') ? '&' : '?';
  const url = `${MASSIVE_API_BASE_URL}${path}${separator}apiKey=${CONFIG.MASSIVE_API_KEY}`;

  const response = await fetch(url);
  if (!response.ok) {
    throw new MassiveApiError(response.status, `Massive API error: ${response.status}`);
  }

  return response.json();
}

export async function getStockQuote(symbol: string): Promise<StockBar | null> {
  const data = await massiveGet<MassiveStockResponse>(
    `/v2/aggs/ticker/${encodeURIComponent(symbol)}/prev`
  );
  return data.results?.[0] ?? null;
}

interface MassiveAggBar {
  t: number;
  c: number;
}

interface MassiveAggResponse {
  results?: MassiveAggBar[];
}

interface MassiveOptionQuote {
  bid_price?: number;
  ask_price?: number;
  sip_timestamp?: number;
}

interface MassiveOptionQuoteResponse {
  results?: MassiveOptionQuote[];
}

export async function getOptionHistoricalPrices(
  occSymbols: string[],
  calendarDays: number,
  concurrency = 5
): Promise<Record<string, HistoricalClose[]>> {
  const uniqueSymbols = [...new Set(occSymbols.map(normalizeOccSymbol).filter(Boolean))];
  const results: Record<string, HistoricalClose[]> = {};
  const end = new Date();
  const start = new Date(end);
  start.setUTCDate(start.getUTCDate() - calendarDays);
  let index = 0;

  async function worker(): Promise<void> {
    while (index < uniqueSymbols.length) {
      const symbol = uniqueSymbols[index++];
      try {
        results[symbol] = await getOptionHistoricalPriceWithRetry(symbol, start, end);
      } catch (error) {
        console.warn(`Failed to get option history for ${symbol}:`, error);
        results[symbol] = [];
      }
    }
  }

  await Promise.all(Array.from({ length: Math.min(concurrency, uniqueSymbols.length) }, worker));
  return results;
}

async function getOptionHistoricalPriceWithRetry(symbol: string, start: Date, end: Date, retries = 2): Promise<HistoricalClose[]> {
  let lastError: unknown;

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      return await getOptionHistoricalPrice(symbol, start, end);
    } catch (error) {
      lastError = error;
      if (attempt < retries) {
        await delay(250 * (attempt + 1));
      }
    }
  }

  throw lastError;
}

async function getOptionHistoricalPrice(symbol: string, start: Date, end: Date): Promise<HistoricalClose[]> {
  const ticker = `O:${symbol}`;
  const data = await massiveGet<MassiveAggResponse>(
    `/v2/aggs/ticker/${encodeURIComponent(ticker)}/range/1/day/${formatDate(start)}/${formatDate(end)}?adjusted=true&sort=desc&limit=5000`
  );

  return (data.results || [])
    .filter(bar => bar.t && bar.c != null)
    .map(bar => ({
      date: formatDate(new Date(bar.t)),
      close: Number(bar.c),
      source: 'trade_close' as const,
      confidence: 'low' as const,
    }));
}

export async function getOptionHistoricalQuoteMidpoints(
  occSymbols: string[],
  dates: string[],
  concurrency = 20
): Promise<Record<string, HistoricalClose[]>> {
  const uniqueSymbols = [...new Set(occSymbols.map(normalizeOccSymbol).filter(Boolean))];
  const uniqueDates = [...new Set(dates)]
    .filter(date => /^\d{4}-\d{2}-\d{2}$/.test(date))
    .sort();
  const results: Record<string, HistoricalClose[]> = Object.fromEntries(
    uniqueSymbols.map(symbol => [symbol, []])
  );
  const jobs = uniqueSymbols.flatMap(symbol => uniqueDates.map(date => ({ symbol, date })));
  let index = 0;

  async function worker(): Promise<void> {
    while (index < jobs.length) {
      const job = jobs[index++];
      const mark = await getOptionHistoricalQuoteMidpointWithRetry(job.symbol, job.date).catch(error => {
        console.warn(`Failed to get option quote midpoint for ${job.symbol} on ${job.date}:`, error);
        return null;
      });
      if (mark) {
        results[job.symbol].push(mark);
      }
    }
  }

  await Promise.all(Array.from({ length: Math.min(concurrency, jobs.length) }, worker));

  for (const symbol of uniqueSymbols) {
    results[symbol].sort((a, b) => b.date.localeCompare(a.date));
  }

  return results;
}

async function getOptionHistoricalQuoteMidpointWithRetry(symbol: string, date: string, retries = 2): Promise<HistoricalClose | null> {
  let lastError: unknown;

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      return await getOptionHistoricalQuoteMidpoint(symbol, date);
    } catch (error) {
      lastError = error;
      if (attempt < retries) {
        await delay(200 * (attempt + 1));
      }
    }
  }

  throw lastError;
}

async function getOptionHistoricalQuoteMidpoint(symbol: string, date: string): Promise<HistoricalClose | null> {
  const ticker = `O:${symbol}`;
  const data = await massiveGet<MassiveOptionQuoteResponse>(
    `/v3/quotes/${encodeURIComponent(ticker)}?timestamp=${date}&limit=1&sort=sip_timestamp&order=desc`
  );
  const quote = data.results?.[0];
  const bid = Number(quote?.bid_price);
  const ask = Number(quote?.ask_price);
  if (!Number.isFinite(bid) || !Number.isFinite(ask) || bid <= 0 || ask <= 0 || ask < bid) {
    return null;
  }

  const close = (bid + ask) / 2;
  const spreadPct = (ask - bid) / close;

  return {
    date,
    close,
    source: 'quote_mid',
    confidence: getQuoteConfidence(spreadPct),
  };
}

function getQuoteConfidence(spreadPct: number): HistoricalClose['confidence'] {
  if (!Number.isFinite(spreadPct)) return 'low';
  if (spreadPct <= 0.05) return 'high';
  if (spreadPct <= 0.15) return 'medium';
  return 'low';
}

export function normalizeOccSymbol(symbol: string): string {
  return symbol.trim().replace(/^O:/i, '').replace(/\s+/g, '').toUpperCase();
}

function formatDate(date: Date): string {
  return date.toISOString().split('T')[0];
}

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export interface OptionsQueryParams {
  contractType: 'call' | 'put';
  minStrike: number;
  maxStrike: number;
  minExpiration: string;
  maxExpiration: string;
  limit?: number;
}

export async function getOptionsChain(
  symbol: string,
  params: OptionsQueryParams
): Promise<OptionSnapshot[]> {
  const queryParams = new URLSearchParams({
    contract_type: params.contractType,
    'strike_price.gte': params.minStrike.toString(),
    'strike_price.lte': params.maxStrike.toString(),
    'expiration_date.gte': params.minExpiration,
    'expiration_date.lte': params.maxExpiration,
    limit: (params.limit ?? 250).toString(),
  });

  const data = await massiveGet<MassiveOptionsResponse>(
    `/v3/snapshot/options/${encodeURIComponent(symbol)}?${queryParams}`
  );

  return data.results ?? [];
}

export interface OptionQuoteGreeks {
  delta: number;
  gamma: number;
  theta: number;
  vega: number;
}

export interface OptionQuote {
  occSymbol: string;
  fmv: number | null;
  close: number | null;
  underlying: string;
  strike: number;
  expiration: string;
  type: 'call' | 'put';
  underlyingPrice: number | null;
  greeks: OptionQuoteGreeks | null;
  iv: number | null;
}

/**
 * Parse OCC symbol to extract components
 * Format: "NVDA  260515P00190000" = NVDA, 2026-05-15, PUT, $190
 */
function parseOccSymbol(occSymbol: string): {
  underlying: string;
  expiration: string;
  type: 'call' | 'put';
  strike: number;
} | null {
  const trimmed = occSymbol.trim().replace(/^O:/, '').replace(/\s+/g, '');
  if (trimmed.length < 15) return null;

  const strikeStr = trimmed.slice(-8);
  const typeChar = trimmed.slice(-9, -8);
  const dateStr = trimmed.slice(-15, -9);
  const underlying = trimmed.slice(0, -15);

  if (!underlying || !/^\d{6}$/.test(dateStr) || !['C', 'P'].includes(typeChar)) {
    return null;
  }

  const year = 2000 + parseInt(dateStr.slice(0, 2), 10);
  const month = parseInt(dateStr.slice(2, 4), 10);
  const day = parseInt(dateStr.slice(4, 6), 10);
  const expiration = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  const strike = parseInt(strikeStr, 10) / 1000;

  return {
    underlying,
    expiration,
    type: typeChar === 'C' ? 'call' : 'put',
    strike,
  };
}

/**
 * Get FMV quotes for options by fetching from options snapshot endpoint
 * Groups by underlying to minimize API calls
 */
export async function getOptionQuotes(occSymbols: string[]): Promise<Map<string, OptionQuote>> {
  const results = new Map<string, OptionQuote>();

  // Parse all OCC symbols and group by underlying
  const symbolsByUnderlying = new Map<string, Array<{ occ: string; parsed: ReturnType<typeof parseOccSymbol> }>>();

  for (const occ of occSymbols) {
    const parsed = parseOccSymbol(occ);
    if (!parsed) continue;

    const existing = symbolsByUnderlying.get(parsed.underlying) || [];
    existing.push({ occ, parsed });
    symbolsByUnderlying.set(parsed.underlying, existing);
  }

  // Fetch options for each underlying
  for (const [underlying, symbols] of symbolsByUnderlying) {
    try {
      // Find the date range needed
      const expirations = symbols.map(s => s.parsed!.expiration).sort();
      const minExp = expirations[0];
      const maxExp = expirations[expirations.length - 1];

      // Find strike range
      const strikes = symbols.map(s => s.parsed!.strike);
      const minStrike = Math.min(...strikes);
      const maxStrike = Math.max(...strikes);

      // Fetch calls and puts separately
      const [calls, puts] = await Promise.all([
        getOptionsChain(underlying, {
          contractType: 'call',
          minStrike: minStrike - 1,
          maxStrike: maxStrike + 1,
          minExpiration: minExp,
          maxExpiration: maxExp,
          limit: 250,
        }).catch(() => []),
        getOptionsChain(underlying, {
          contractType: 'put',
          minStrike: minStrike - 1,
          maxStrike: maxStrike + 1,
          minExpiration: minExp,
          maxExpiration: maxExp,
          limit: 250,
        }).catch(() => []),
      ]);

      const allSnapshots = [...calls, ...puts];

      // Match snapshots to OCC symbols
      for (const { occ, parsed } of symbols) {
        if (!parsed) continue;

        // Find matching snapshot
        const snapshot = allSnapshots.find(s =>
          s.details.strike_price === parsed.strike &&
          s.details.expiration_date === parsed.expiration &&
          s.details.contract_type === parsed.type
        );

        if (snapshot) {
          results.set(occ, {
            occSymbol: occ,
            fmv: parseFloat(snapshot.fmv) || null,
            close: snapshot.day?.close ?? null,
            underlying: parsed.underlying,
            strike: parsed.strike,
            expiration: parsed.expiration,
            type: parsed.type,
            underlyingPrice: snapshot.underlying_asset?.price ?? null,
            greeks: snapshot.greeks ? {
              delta: snapshot.greeks.delta,
              gamma: snapshot.greeks.gamma,
              theta: snapshot.greeks.theta,
              vega: snapshot.greeks.vega,
            } : null,
            iv: snapshot.implied_volatility ?? null,
          });
        }
      }
    } catch (error) {
      console.error(`Failed to get options for ${underlying}:`, error);
    }
  }

  return results;
}
